/*
<purpose>
  part 7, 2026-10-11 („what you can recognise, you can find"): maps the clerk's typed search term to
  the served PRIMARY and SECONDARY ids whose FACE contains it, so a ticket is found by the word its
  pill wears („Stammdaten" finds the tickets classified `stammdaten`), not only by the text of the
  mail. Both list pages send the result beside `search` as `searchPrimary` / `searchSecondary`;
  core OR's them INTO the search predicate (widening) and ignores them without a `search` term.
</purpose>
<dependencies>useCategoryMeta() — `categoryKeys` / `label` for the primary axis (unclassified
  included: its pill is recognisable too), `secondaryAxisKeys` / `secondaryAxisText` for the
  secondary one. The faces come from the served vocabulary; this file spells no word.</dependencies>
<constraints>
  - Case-insensitive (de-DE), SUBSTRING, trimmed term.
  - Fewer than 3 characters finds nothing: a two-letter term is inside half the vocabulary and
    would widen the search into noise.
  - Ids are returned in the vocabulary's own order, each at most once.
  - pillFilterParams (below) is the ONE assembly of the pill filters both list pages send — flag,
    the „Formular" facet and these search ids — so the two pages cannot word the same filter set two ways.
    Empty lists become `undefined` (omitted on the wire, and a stable facets request key).
</constraints>
*/
import type { CategoryMeta } from '../context/ClassificationSchemaContext';
import type { PillFilterParams } from './api';
import type { FlagId } from '../types';

export const SEARCH_FACE_MIN_LENGTH = 3;

export function searchFaceIds(term: string, cm: CategoryMeta): { searchPrimary: string[]; searchSecondary: string[] } {
  const needle = term.trim().toLocaleLowerCase('de-DE');
  if (needle.length < SEARCH_FACE_MIN_LENGTH) return { searchPrimary: [], searchSecondary: [] };
  const matches = (face: string) => face.toLocaleLowerCase('de-DE').includes(needle);
  return {
    searchPrimary: cm.categoryKeys.filter(id => matches(cm.label(id))),
    searchSecondary: cm.secondaryAxisKeys.filter(id => matches(cm.secondaryAxisText(id))),
  };
}

const nonEmpty = (ids: string[]) => (ids.length > 0 ? ids : undefined);

/** The pill filters a list page holds, as the params every read of that list sends. */
export function pillFilterParams(
  state: { flagFilter: FlagId[]; search: string; hasFormFilter: boolean },
  cm: CategoryMeta,
): PillFilterParams {
  const ids = searchFaceIds(state.search, cm);
  return {
    flag: nonEmpty(state.flagFilter),
    searchPrimary: nonEmpty(ids.searchPrimary),
    searchSecondary: nonEmpty(ids.searchSecondary),
    // The „Formular" facet (part 7 fix loop, 2026-10-12): `true` or omitted — never `false` on the wire.
    hasForm: state.hasFormFilter ? true : undefined,
  };
}
